import { ClickAwayListener, Popper } from "@mui/material";
import { type FC } from "react";

import type { IMarket } from "~/types/event";

import { MarketChips } from "./components/MarketChips";
import { MarketsDropdownMenu } from "./components/MarketsDropdownMenu";
import * as Styled from "./MarketsDropdown.styles";
import { useMarketsDropdown } from "./useMarketsDropdown";

export interface IMarketsDropdownProps {
  markets: IMarket[];
  selections: string[];
  isConsensusMode: boolean;
  onToggle: (marketId: string) => void;
  onSelectConsensus: (marketId: string) => void;
}

export const MarketsDropdown: FC<IMarketsDropdownProps> = ({
  markets,
  selections,
  isConsensusMode,
  onToggle,
  onSelectConsensus,
}) => {
  const { anchorEl, setAnchorEl, isOpen, handleToggle, handleClose } =
    useMarketsDropdown();

  // Escape with the menu open closes the menu, not whatever dialog holds it.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (isOpen && event.key === "Escape") {
      event.stopPropagation();
      handleClose();
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Styled.Root ref={setAnchorEl} onKeyDown={handleKeyDown}>
        <MarketChips
          markets={markets}
          selections={selections}
          onToggle={onToggle}
        />

        <Styled.TriggerButton
          aria-label="Select markets"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={handleToggle}
        >
          <Styled.ArrowIcon $open={isOpen} />
        </Styled.TriggerButton>

        <Popper
          open={isOpen}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <Styled.DropdownPaper>
            <MarketsDropdownMenu
              markets={markets}
              selections={selections}
              isConsensusMode={isConsensusMode}
              onToggle={onToggle}
              onSelectConsensus={onSelectConsensus}
              onClose={handleClose}
            />
          </Styled.DropdownPaper>
        </Popper>
      </Styled.Root>
    </ClickAwayListener>
  );
};
